<template>
    <b-list-group-item
        :id="sectionId"
        class="article-body rounded-0"
    >
        <div class="d-flex">
            <div class="h6">
                {{ headerTitle }}
            </div>
            <div class="d-flex ml-auto">
                <b-btn
                    size="sm"
                    variant="outline-info"
                    @click="newArticleQuestion()"
                    class="ml-2"
                    :disabled="!canUpdateArticle || disabled || usedByLockedPublication"
                >
                    <font-awesome-icon :icon="['far', 'plus']" fixed-width />
                    {{ addButtonLabel }}
                </b-btn>

                <slot name="header-actions" />
            </div>
        </div>
        <div
            v-for="(question, index) in articleData.questions"
            :key="index"
            class="mt-4"
        >
            <div class="d-flex align-items-center">
                <div
                    class="pr-3 flex-column d-flex align-self-stretch"
                    style="justify-content: space-between; border-right: 1px solid #999"
                >
                    <b-btn
                        size="sm"
                        variant="outline-primary"
                        class="mt-3"
                        :disabled="index <= 0 || !canUpdateArticle || disabled || usedByLockedPublication"
                        @click="questionSortUp(question)"
                    >
                        <font-awesome-icon :icon="['fas', 'caret-up']" fixed-width />
                    </b-btn>
                    <b-btn
                        size="sm"
                        variant="outline-primary"
                        class="mb-3"
                        :disabled="index >= articleData.questions.length - 1 || !canUpdateArticle || disabled || usedByLockedPublication"
                        @click="questionSortDown(question)"
                    >
                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                    </b-btn>
                </div>
                <div class="flex-grow-1">
                    <div v-if="question.loading">
                        <b-spinner small label="Loading..."></b-spinner>
                    </div>
                    <div v-else>
                        <Question
                            :publication="publication"
                            :unit="unit"
                            :article="article"
                            :question="question"
                            :available_standards="availableStandards"
                            :state_grade="stateGrade"
                            @update="updateArticleQuestion"
                            @unlinkQuestion="removeArticleQuestion"
                            :disabled="disabled || usedByLockedPublication"
                        />
                        <div v-if="question.narration_loading">
                            <content-placeholders>
                                <content-placeholders-text :lines="3" />
                            </content-placeholders>
                        </div>
                        <div
                            v-else-if="question.type == 'text_block' && question.narration && question.narration.url"
                        >
                            <div class="ml-3 h6">
                                Narration
                            </div>
                            <b-card no-body>
                                <b-card-body>
                                    <div class="d-flex align-items-center">
                                        <div>
                                            <audio ref="narration-audio" class="d-flex mb-0" controls>
                                                <source :src="question.narration.url" type="audio/mpeg">
                                                Your browser does not support the audio element.
                                            </audio>
                                        </div>
                                        <div class="d-flex align-items-center ml-auto">
                                            <div v-if="question.narration.alignment_status" class="mr-3">
                                                <span
                                                    class="badge"
                                                    :class="narration_badge(question.narration)"
                                                >
                                                    {{ narration_badge_text(question.narration) }}
                                                </span>
                                                <span
                                                    class="badge badge-light border ml-1"
                                                    :title="'Aligned by ' + narration_driver_label(question.narration)"
                                                >
                                                    {{ narration_driver_label(question.narration) }}
                                                </span>
                                            </div>
                                            <div>
                                                <b-dropdown
                                                    size="sm"
                                                    variant="secondary"
                                                    :disabled="se_locked"
                                                    dropleft
                                                >
                                                    <template #button-content>
                                                        <font-awesome-icon :icon="['fad', 'cog']" />
                                                    </template>
                                                    <b-dropdown-item
                                                        v-if="question.narration.returned && !question.narration.approved"
                                                        @click="approveNarration(question.narration)"
                                                        :disabled="!canUpdateArticle || disabled || usedByLockedPublication"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fad', 'check']"
                                                            class="text-success"
                                                            fixed-width
                                                        />
                                                        Approve
                                                    </b-dropdown-item>
                                                    <b-dropdown-item
                                                        @click="reparseNarration(question.narration)"
                                                        :disabled="!canUpdateArticle || disabled || usedByLockedPublication"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fad', 'sync-alt']"
                                                            class="text-warning"
                                                            fixed-width
                                                        />
                                                        Re-parse
                                                    </b-dropdown-item>
                                                    <b-dropdown-item
                                                        @click="testNarrationAudio(question.narration)"
                                                        :disabled="!canUpdateArticle || disabled || usedByLockedPublication"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fad', 'waveform-path']"
                                                            class="text-secondary"
                                                            fixed-width
                                                        />
                                                        Test Synced Audio
                                                    </b-dropdown-item>
                                                    <b-dropdown-item
                                                        @click="deleteNarration(question.narration, question)"
                                                        :disabled="!canUpdateArticle || disabled || usedByLockedPublication"
                                                    >
                                                        <font-awesome-icon
                                                            :icon="['fad', 'trash']"
                                                            class="text-danger"
                                                            fixed-width
                                                        />
                                                        Delete
                                                    </b-dropdown-item>
                                                </b-dropdown>
                                            </div>
                                        </div>
                                    </div>
                                </b-card-body>
                            </b-card>
                        </div>
                        <div v-else-if="question.type == 'text_block'">
                            <div v-if="uploading_file">
                                <b-spinner label="Loading..."></b-spinner>
                            </div>
                            <div v-else>
                                <b-form-file
                                    v-model="narration_file"
                                    accept="audio/*"
                                    browse-text="Upload File"
                                    :disabled="se_locked || !canUpdateArticle || disabled || usedByLockedPublication"
                                    @input="uploadNarration($event, question)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <narration-audio-sync-modal
            v-model="show_narration_audio_sync_modal"
            :article="article"
            :narration="currentNarration"
            @approve="approveNarration(currentNarration)"
            @hide="show_narration_audio_sync_modal = false"
        />

        <question-modal
            v-model="show_question_modal"
            :unit="unit"
            :article="article"
            :question="question"
            :available_standards="availableStandards"
            :state_grade="stateGrade"
            @create="createArticleQuestion"
            @hide="show_question_modal = false"
        />
    </b-list-group-item>
</template>

<script>
import moment from 'moment'
import NarrationAudioSyncModal from '@/components/modals/article/NarrationAudioSyncModal'
import Question from '@/components/article/Question'
import QuestionModal from '@/components/modals/questions/QuestionModal'

export default {
    props: {
        publication: {
            type: Object,
            required: true,
        },
        unit: {
            type: Object,
            required: true,
        },
        article: {
            type: Object,
            required: true,
        },
        articleData: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        availableStandards: {
            type: Array,
            default: () => [],
        },
        stateGrade: {
            default: null,
        },
        canUpdateArticle: {
            type: Boolean,
            default: false,
        },
        usedByLockedPublication: {
            type: Boolean,
            default: false,
        },
        headerTitle: {
            type: String,
            required: true,
        },
        addButtonLabel: {
            type: String,
            required: true,
        },
        sectionId: {
            type: String,
            required: true,
        },
    },
    components: {
        NarrationAudioSyncModal,
        Question,
        QuestionModal,
    },
    data: () => ({
        currentNarration: {},
        narration_file: null,
        question: {},
        show_narration_audio_sync_modal: false,
        show_question_modal: false,
        uploading_file: false,
    }),
    computed: {
        se_locked() {
            return Boolean(
                this.publication &&
                this.unit &&
                (this.publication.se_locked || this.unit.se_locked)
            )
        },
    },
    methods: {
        emitRefreshArticle() {
            this.$emit('refresh-article')
        },
        narration_badge(narration) {
            if (narration.approved) {
                return 'badge-success'
            }
            if (narration.returned) {
                return 'badge-primary'
            }
            if (narration.failed) {
                return 'badge-danger'
            }
            if (narration.alignment_status === 'not_submitted') {
                return 'badge-secondary'
            }
            if (['in_progress', 'in_process'].includes(narration.alignment_status)) {
                return 'badge-warning'
            }
            return 'badge-danger'
        },
        narration_badge_text(narration) {
            if (narration.approved) {
                return "Audio QC'd"
            }
            if (narration.returned) {
                return 'Audio Synced'
            }
            if (narration.failed) {
                return 'Audio Failed'
            }
            if (narration.alignment_status === 'not_submitted') {
                return 'Not Processed'
            }
            if (['in_progress', 'in_process'].includes(narration.alignment_status)) {
                return 'Audio In Progress'
            }
            return 'Audio Failed'
        },
        // Which engine produced this narration. Narrations predating driver
        // selection carry no driver and were all produced by 3Play.
        narration_driver_label(narration) {
            const labels = { cadence: 'Cadence', threeplay: '3Play' }
            const driver = narration.alignment_driver || 'threeplay'

            return labels[driver] || driver
        },
        async questionSortUp(question) {
            const index = this.articleData.questions.indexOf(question)
            if (index <= 0) {
                return
            }

            const previousQuestion = this.articleData.questions[index - 1]

            try {
                this.$set(this.articleData.questions, index, previousQuestion)
                this.$set(this.articleData.questions, index - 1, question)
                await this.axios.post(`/api/articles/reorder_question/${this.articleData.id}`, {
                    question_id: question.id,
                    old_order_number: index + 1,
                    new_order_number: index,
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                })
                this.emitRefreshArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to move question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async questionSortDown(question) {
            const index = this.articleData.questions.indexOf(question)
            if (index >= this.articleData.questions.length - 1) {
                return
            }

            const nextQuestion = this.articleData.questions[index + 1]

            try {
                this.$set(this.articleData.questions, index, nextQuestion)
                this.$set(this.articleData.questions, index + 1, question)
                await this.axios.post(`/api/articles/reorder_question/${this.articleData.id}`, {
                    question_id: question.id,
                    old_order_number: index + 1,
                    new_order_number: index + 2,
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                })
                this.emitRefreshArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to move question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async getNarration(question = null) {
            const narrationTarget = question || this.article
            this.$set(narrationTarget, 'narration_loading', true)

            const url = '/api/articles/narration/' + this.article.article_id + (question ? '/' + question.id : '')

            try {
                const response = await this.axios.get(url)

                if (response.data) {
                    this.$set(narrationTarget, 'narration', response.data)
                } else {
                    this.$set(narrationTarget, 'narration', {})
                }
            } catch (e) {
                this.$set(narrationTarget, 'narration', {})
            } finally {
                this.$set(narrationTarget, 'narration_loading', false)
            }
        },
        async updateNarrationStatus(narration, approved) {
            try {
                const response = await this.axios.post('/api/articles/update_narration_status/' + narration.id, {
                    approved,
                })

                if (response) {
                    await this.getNarration()
                    narration.approved = approved === false ? null : 1
                    this.emitRefreshArticle()
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update narration status: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async approveNarration(narration) {
            const result = await this.$swal({
                html: '<p>Are you sure you want to approve the narration audio/text sync for this article?</p><div class="text-warning">It is recommended that you verify that the audio is synced correctly first.</div>',
                showCancelButton: true,
                confirmButtonText: 'Approve Audio Sync',
                confirmButtonColor: '#27AE60',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })

            if (result.value) {
                await this.updateNarrationStatus(narration, true)
                this.show_narration_audio_sync_modal = false
            }
        },
        async reparseNarration(narration) {
            const result = await this.$swal({
                html: 'Are you sure you want to re-parse the text for this article?',
                showCancelButton: true,
                confirmButtonText: 'Re-parse Narration Text',
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })

            if (result.value) {
                const response = await this.axios.post('/api/articles/parse_narration_text/' + narration.id)

                if (response) {
                    await this.updateNarrationStatus(narration, false)
                    await this.getNarration()
                    this.emitRefreshArticle()
                }
            }
        },
        testNarrationAudio(narration) {
            this.currentNarration = narration
            this.show_narration_audio_sync_modal = true

            if (this.$refs['narration-audio']) {
                this.$refs['narration-audio'].pause()
            }
        },
        async deleteNarration(narration, question = null) {
            const result = await this.$swal({
                html: 'Are you sure you want to delete the narration for this article?',
                showCancelButton: true,
                confirmButtonText: 'Delete Narration',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })

            if (result.value) {
                const response = await this.axios.post('/api/articles/delete_narration/' + narration.id)

                if (response) {
                    await this.getNarration()
                    if (question) {
                        question.narration = {}
                    } else {
                        this.article.narration = {}
                    }
                    this.emitRefreshArticle()
                }
            }
        },
        newArticleQuestion() {
            this.question = {
                type: 'multiple_choice'
            }
            this.show_question_modal = true
        },
        async createArticleQuestion(question) {
            try {
                this.$set(question, 'loading', true)

                await this.axios.post(`/api/articles/create_question/${this.article.article_id}`, {
                    type: question.type,
                    content: question.content,
                    print_view: question.print_view,
                    standards: question.standards,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                })

                this.show_question_modal = false
                this.emitRefreshArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to create question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async updateArticleQuestion() {
            try {
                this.emitRefreshArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async removeArticleQuestion() {
            try {
                this.emitRefreshArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to remove question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async uploadNarration(event, question = null) {
            const file = this.getNarrationUploadFile(event)
            if (!file) {
                return
            }

            const result = await this.$swal({
                html: '<p>Is this audio intended for a comic strip?</p><div class="text-warning">Comic strip audio will not be submitted to Three Play.</div>',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                confirmButtonColor: '#27AE60',
                cancelButtonText: 'No',
                focusCancel: true,
                reverseButtons: true
            })

            const is_comic_audio = Boolean(result.value)
            const form_data = new FormData()

            form_data.append('audio', file, file.name)
            form_data.append('article_id', this.article.article_id)
            form_data.append('is_comic_audio', is_comic_audio ? 'true' : 'false')

            if (question && question.id) {
                form_data.append('question_id', question.id)
            }

            this.uploading_file = true

            try {
                const response = await this.axios.post('/api/articles/create_narration/' + this.publication.id, form_data)

                await this.getNarration(question)
                if (question) {
                    question.narration = response.data
                } else {
                    this.article.narration = response.data
                }
                this.emitRefreshArticle()
            } finally {
                this.uploading_file = false
                this.narration_file = null
            }
        },
        getNarrationUploadFile(event) {
            if (event && event.target && event.target.files && event.target.files.length) {
                return event.target.files[0]
            }

            if (event && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
                return event.dataTransfer.files[0]
            }

            if (typeof FileList !== 'undefined' && event instanceof FileList && event.length) {
                return event[0]
            }

            if (typeof File !== 'undefined' && event instanceof File) {
                return event
            }

            if (typeof File !== 'undefined' && this.narration_file instanceof File) {
                return this.narration_file
            }

            return null
        },
    },
}
</script>

<style lang="scss" scoped>
.card {
    &.collapsed {
        .card-header {
            border-radius: .25rem;
        }
    }

    .card-header {
        border-bottom: 0;
    }

    .list-group {
        .list-group-item {
            &:first-child {
                // border-radius: 0;
            }

            .list-group {
                .list-group-item {
                    // border-radius: inherit;
                }
            }
        }
    }
}
</style>
